import type {
  CircleMarker,
  GeoJSON as GeoJSONLayer,
  Map,
  Marker
} from 'leaflet';
import { circleMarker, geoJSON, icon, LatLng, marker } from 'leaflet';
import { useEffect, useState } from 'react';

import { selectExternalNetworkInjection } from '@/features/execute-calculation-set/mediumVoltageCalculations/selectExternalNetworkInjection';
import type {
  DomainGuid,
} from '@/features/execute-calculation-set/overmind/calculation';
import { useAppState } from '@/overmind';
import type {
  FeaturesInLayer,
  FeatureWithIDs
} from '@/overmind/effects/adapters/baseadapter';

import '../assets/externalNetworkInjection.css';

import { RenderedElement } from '../utils/MapElementDesigner';
import { createColorStyles } from '../utils/createColorStyles';
import type {
  ColorStyles,
  HighlightStates,
  StyleMapElementFunction
} from '../utils/styleMapElement';
import { styleMapElementForAllCalculations } from '../utils/styleMapElement';
import { useHighlightedFeatures } from './useHighlightedFeatures';
import { createMyOwnFeaturesInLayers } from './createMyOwnFeaturesInLayers';
import { OrderedLayer, OrderedLayerImportance } from './OrderLayer';
import { IsGridElementFunction, useIsGridElementType } from './useIsGridElementType';
import { createGetTransformerIcon, getExternalNetworkInjectionIcon, GetTransformerIcon } from './getIcons';
import useFeatureToggles from '@/common/hooks/feature-toggles';

const useSelectedGridElementId = () => {
  const state = useAppState().calculation.selectedGridElement;
  return state.type === 'SELECTED_GRID_ELEMENT'
    ? state.gridElement.objectId
    : undefined;
};

export const useExternalNetworkInjectionId = () => {
  const state = useAppState().calculation.calculationTactic;
  return state.current === 'LOADED' && state.value.status === 'Completed'
    ? selectExternalNetworkInjection(
        state.value.calculationSetResult.grid.functional
      )?.objectId
    : undefined;
};

const colorStyles = createColorStyles({
  selectedElement: '#2ba3ab',
  subnet: '#6db7ae'
});

export type TacticResultLayers = {
  layers: GeoJSONLayer[];
  idsInLayers: DomainGuid[];
};

const withObjectId = (marker: CircleMarker | Marker, objectId: DomainGuid) => {
  // @ts-ignore
  marker.objectId = objectId;
  return marker;
};

export function useMvTacticResultLayers(
  map: Map | null,
  onFeatureSelected: (objectId: DomainGuid) => void
): [TacticResultLayers] {
  const featureLayers = useAppState().arcgis.featureGeometries.value;
  const selectedElementId = useSelectedGridElementId();
  const externalNetworkInjectionId = useExternalNetworkInjectionId();
  const isGridElementType = useIsGridElementType();
  const [statuses] = useHighlightedFeatures();
  const tacticResultBrowsingFilter =
    useAppState().calculation.tacticResultBrowsingFilter;

  const calculationContext = useAppState().calculation.calculationContext;
  const { shouldUseInitiatingObjectHighlighting } = useFeatureToggles();
  const useInitiatingObjectHighlighting = shouldUseInitiatingObjectHighlighting();

  const [layers, setLayers] = useState<TacticResultLayers>({
    layers: [],
    idsInLayers: []
  });

  useEffect(() => {
    const newLayers: TacticResultLayers = {
      layers: [],
      idsInLayers: []
    };
    if (map && featureLayers && calculationContext.type === "AVAILABLE") {
      const getTransformerIcon = createGetTransformerIcon(useInitiatingObjectHighlighting)
      const orderedLayers = mapToStyledLayers(
        featureLayers,
        selectedElementId,
        externalNetworkInjectionId,
        calculationContext.calculationContext.relatedObjectId,
        statuses,
        styleMapElementForAllCalculations(useInitiatingObjectHighlighting),
        isGridElementType,
        getTransformerIcon
      );

      newLayers.layers = orderedLayers.map(layer => layer.toGeoJson());

      newLayers.idsInLayers = featureLayers
        .flatMap(featureLayer => featureLayer.features)
        .map(feature => feature.gc.globalId);
      orderedLayers.forEach(layer => layer.addTo(map));
      orderedLayers.forEach(layer => {
        layer.toGeoJson().eachLayer(l =>
          l.on('click', (event: any) => {
            onFeatureSelected(event.target.objectId);
          })
        );
      });
      // @ts-ignore
      window.__map__ = map;
    }
    setLayers(newLayers);
    return () => {
      if (map && newLayers.layers) {
        newLayers.layers.forEach(layer => layer.removeFrom(map));
      }
    };
  }, [
    map,
    featureLayers,
    statuses,
    selectedElementId,
    externalNetworkInjectionId,
    tacticResultBrowsingFilter,
    calculationContext,
    useInitiatingObjectHighlighting
  ]);

  return [layers];
}

function mapToStyledLayers(
  featuresInLayers: FeaturesInLayer[],
  selectedElementId: DomainGuid | undefined,
  startingElementId: DomainGuid | undefined,
  initatingObjectId: DomainGuid,
  statuses: HighlightStates,
  styleMapElement: StyleMapElementFunction,
  isGridElementType: IsGridElementFunction,
  getTransformerIcon: GetTransformerIcon
): OrderedLayer[] {
  return createMyOwnFeaturesInLayers(featuresInLayers, statuses, startingElementId)
    .map(layerWithImportance =>
      createStyledGeoJsonLayer(
        layerWithImportance,
        selectedElementId,
        startingElementId,
        initatingObjectId,
        statuses,
        colorStyles,
        styleMapElement,
        isGridElementType,
        getTransformerIcon
      )
    );
}

function createStyledGeoJsonLayer(
  layerWithImportance: {
    layer: FeaturesInLayer;
    importance: OrderedLayerImportance;
  },
  selectedElementId: DomainGuid | undefined,
  externalNetworkInjectionId: DomainGuid | undefined,
  initatingObjectId: DomainGuid,
  statuses: HighlightStates,
  colorStyles: ColorStyles,
  styleMapElement: StyleMapElementFunction,
  isGridElement: IsGridElementFunction,
  getTransformerIcon: GetTransformerIcon
) {
  const paneId = layerWithImportance.importance.sequenceNumber;
  const geoJSONLayer = geoJSON(layerWithImportance.layer.features, {
    pane: paneId,
    style: featureToStyle => {
      const color = styleMapElement(
        RenderedElement.fromFeature(featureToStyle as FeatureWithIDs),
        selectedElementId,
        initatingObjectId,
        statuses,
        colorStyles,
        externalNetworkInjectionId
      ) ?? {
        opacity: 0
      };
      return color;
    },
    pointToLayer: function () {
      try {
        /* const resultStyle = styleMapElement(
          featureToStyle as FeatureWithIDs,
          selectedElementId,
          statuses,
          colorStyles,
          externalNetworkInjectionId
        );
        if (resultStyle) {
          return circleMarker(latlng, resultStyle);
        }
        return undefined as unknown as CircleMarker; */
        return undefined as unknown as CircleMarker;
      } catch (err) {
        console.log('error in pointToLayer: ' + JSON.stringify(err));
        return undefined as unknown as CircleMarker;
      }
    }
  });

  const externalNetworkInjectionFeature =
    layerWithImportance.layer.features.find(
      f => f.gc.globalId === externalNetworkInjectionId
    );
  if (
    externalNetworkInjectionFeature != null &&
    externalNetworkInjectionFeature.geometry.type === 'LineString' &&
    externalNetworkInjectionId !== undefined
  ) {
    const latlng = getCenterLatLng(
      externalNetworkInjectionFeature.geometry.coordinates
    );
    const iconToUse = getExternalNetworkInjectionIcon(
      externalNetworkInjectionId,
      selectedElementId
    );
    if (iconToUse) {
      const externalNetworkInjectionMarker = withObjectId(
        marker(latlng, {
          icon: iconToUse,
          pane: paneId
        }),
        externalNetworkInjectionId
      );
      externalNetworkInjectionMarker.addTo(geoJSONLayer);
    }
  }
  layerWithImportance.layer.features.forEach(feature => {
    if (
      isGridElement('PowerTransformer', feature.gc.globalId) &&
      feature.geometry.type === 'Point'
    ) {
      const [longitude, latitude] = feature.geometry.coordinates;
      const latlng = new LatLng(latitude, longitude);

      // const elementStyle = styleMapElement(
      //   feature as FeatureWithIDs,
      //   selectedElementId,
      //   statuses,
      //   colorStyles,
      //   externalNetworkInjectionId
      // );

      // circleMarker(latlng, elementStyle).addTo(geoJSONLayer);
      const iconToUse = getTransformerIcon(
        feature.gc.globalId,
        selectedElementId,
        initatingObjectId,
        statuses
      );
      if (iconToUse) {
        const powerTransformerMarker = withObjectId(
          marker(latlng, {
            icon: iconToUse,
            pane: paneId
          }),
          feature.gc.globalId
        );
        powerTransformerMarker.addTo(geoJSONLayer);
      }
    }
  });

  layerWithImportance.layer.features.forEach(feature => {
    if (
      isGridElement('Busbar', feature.gc.globalId) &&
      feature.geometry.type === 'LineString'
    ) {
      const latlng = getCenterLatLng(feature.geometry.coordinates);
      const elementStyle = styleMapElement(
        RenderedElement.create(feature.gc.globalId, 'Point'),
        selectedElementId,
        initatingObjectId,
        statuses,
        colorStyles,
        externalNetworkInjectionId
      );
      circleMarker(latlng, { ...elementStyle, pane: paneId }).addTo(
        geoJSONLayer
      );
    }
  });

  layerWithImportance.layer.features.forEach(feature => {
    if (
      isGridElement('EnergyConsumer', feature.gc.globalId) &&
      feature.geometry.type === 'Point'
    ) {
      const [longitude, latitude] = feature.geometry.coordinates;
      const latlng = new LatLng(latitude, longitude);
      const elementStyle = styleMapElement(
        RenderedElement.create(feature.gc.globalId, 'Point'),
        selectedElementId,
        initatingObjectId,
        statuses,
        colorStyles,
        externalNetworkInjectionId
      );
      circleMarker(latlng, { ...elementStyle, pane: paneId }).addTo(
        geoJSONLayer
      );
    }
  });

  return new OrderedLayer(geoJSONLayer, layerWithImportance.importance);
}

export function getCenterLatLng(coordinates: GeoJSON.Position[]) {
  const longitudeMax = Math.max(...coordinates.map(c => c[0]));
  const longitudeMin = Math.min(...coordinates.map(c => c[0]));
  const latitudeMax = Math.max(...coordinates.map(c => c[1]));
  const latitudeMin = Math.min(...coordinates.map(c => c[1]));
  const latitudeCenter = (latitudeMax + latitudeMin) / 2;
  const longitudeCenter = (longitudeMax + longitudeMin) / 2;
  const latlng = new LatLng(latitudeCenter, longitudeCenter);
  return latlng;
}
